import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pagination } from '../components/Pagination';
import {
  productApi,
  productBalanceApi,
  productCategoryApi,
} from '../api/api';
import { useStore } from '../context/StoreContext';
import {
  parseProductBalanceIlist,
  setProductBalanceOnServer,
} from '../utils/productBalanceServer';
import { productCategoryLabelFromRow } from '../utils/productCategoryLabel';
import { swalSuccess, swalError } from '../utils/swal';

const LOW_STOCK = 10;
const LIST_PER_PAGE = 10;

/** Match product ilist parsing (plain array or nested data). */
function parseProductIlist(data) {
  const raw = data?.returnData?.list_of_item;
  if (Array.isArray(raw)) return { items: raw };
  if (raw?.data && Array.isArray(raw.data)) {
    return { items: raw.data };
  }
  return { items: [] };
}

function parseCategoryIlist(data) {
  const root = data?.returnData?.list_of_item;
  if (!root) return { items: [] };
  if (Array.isArray(root)) return { items: root };
  const items = root.data ?? [];
  return { items };
}

function reorderLevelFromBalanceRow(row) {
  const p = row.product && typeof row.product === 'object' ? row.product : null;
  const raw = p?.reorder_level ?? row.reorder_level;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

function mapBalanceRowToProduct(row, categoryNamesById) {
  const pid = row.product_id ?? row.product?.id ?? null;
  const balanceRecordId = row.id != null ? row.id : null;
  const id =
    pid != null
      ? String(pid)
      : balanceRecordId != null
        ? `bal-${balanceRecordId}`
        : `row-${String(row.id ?? '')}`;
  const name =
    row.product?.name ??
    row.name ??
    row.product_name ??
    'Product';
  const barcodeRaw =
    row.product?.barcode ?? row.barcode ?? row.product_barcode;
  const barcode =
    barcodeRaw != null && String(barcodeRaw).trim() !== ''
      ? String(barcodeRaw).trim()
      : undefined;
  const qty = Number(row.balance ?? row.qty ?? 0);
  const updatedAt = row.updated_at || row.created_at || '';
  const productIdNum =
    pid != null && !Number.isNaN(Number(pid)) ? Number(pid) : null;
  const category = productCategoryLabelFromRow(
    row.product ?? row,
    categoryNamesById
  );
  const reorderLevel = reorderLevelFromBalanceRow(row);
  const inactive = row.product?.can_be_sold === 'no';
  return {
    id,
    balanceRecordId,
    productId: productIdNum,
    name,
    barcode,
    qty,
    updatedAt,
    category,
    reorderLevel,
    inactive,
  };
}

/** Server may return multiple balance rows per product; keep the newest for display. */
function pickNewerBalanceRow(a, b) {
  const aid = Number(a.balanceRecordId) || 0;
  const bid = Number(b.balanceRecordId) || 0;
  if (bid !== aid) return bid > aid ? b : a;
  return (a.updatedAt || '') >= (b.updatedAt || '') ? a : b;
}

function dedupeBalanceRowsByProduct(rows, categoryNamesById) {
  const mapped = rows.map((row) =>
    mapBalanceRowToProduct(row, categoryNamesById)
  );
  const byPid = new Map();
  for (const m of mapped) {
    if (m.productId == null) {
      byPid.set(`_orphan_${m.balanceRecordId ?? m.id}`, m);
      continue;
    }
    const key = String(m.productId);
    const prev = byPid.get(key);
    if (!prev) {
      byPid.set(key, m);
      continue;
    }
    byPid.set(key, pickNewerBalanceRow(prev, m));
  }
  return Array.from(byPid.values());
}

export function StockManagement() {
  const { setStock } = useStore();

  const [productOptions, setProductOptions] = useState([]);
  const [productOptionsLoading, setProductOptionsLoading] = useState(false);

  const [categoryItems, setCategoryItems] = useState([]);

  const [quickProductId, setQuickProductId] = useState('');
  const [quickBalance, setQuickBalance] = useState('');
  const [quickSubmitting, setQuickSubmitting] = useState(false);
  const [quickError, setQuickError] = useState(null);

  const [balanceRows, setBalanceRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [listPage, setListPage] = useState(1);
  const [listMeta, setListMeta] = useState({
    mode: 'empty',
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });

  const [searchInput, setSearchInput] = useState('');
  const [searchQ, setSearchQ] = useState('');

  const loadProductOptions = useCallback(async () => {
    setProductOptionsLoading(true);
    try {
      const all = [];
      let page = 1;
      const per = 100;
      for (;;) {
        const resp = await productApi.ilist({
          paginate: true,
          per_page: per,
          page,
        });
        const data = resp?.data ?? null;
        if (!data || data.status !== 'OK') break;
        const { items } = parseProductIlist(data);
        if (!items.length) break;
        all.push(...items);
        if (items.length < per) break;
        page += 1;
      }
      setProductOptions(all);
    } catch {
      setProductOptions([]);
    } finally {
      setProductOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProductOptions();
  }, [loadProductOptions]);

  const categoryNameById = useMemo(() => {
    const m = new Map();
    for (const c of categoryItems) {
      const id = Number(c.id);
      if (Number.isNaN(id)) continue;
      const n = String(c.name ?? '').trim();
      if (n) m.set(id, n);
    }
    return m;
  }, [categoryItems]);

  const loadCategories = useCallback(async () => {
    try {
      const all = [];
      let page = 1;
      const per = 100;
      for (;;) {
        const resp = await productCategoryApi.ilist({
          paginate: true,
          per_page: per,
          page,
        });
        const data = resp?.data ?? null;
        if (!data || data.status !== 'OK') break;
        const { items } = parseCategoryIlist(data);
        if (!items.length) break;
        all.push(...items);
        if (items.length < per) break;
        page += 1;
      }
      setCategoryItems(all);
    } catch {
      setCategoryItems([]);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const fetchBalanceList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const resp = await productBalanceApi.ilist({
        paginate: true,
        per_page: LIST_PER_PAGE,
        page: listPage,
        q: searchQ,
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        setBalanceRows([]);
        setListMeta({
          mode: 'empty',
          total: 0,
          lastPage: 1,
          from: 0,
          to: 0,
        });
        setListError(data?.errorMessage || 'Failed to load stock list.');
        return;
      }
      const parsed = parseProductBalanceIlist(data);
      setBalanceRows(parsed.items);
      setListMeta({
        mode: parsed.mode,
        total: parsed.total,
        lastPage: parsed.lastPage,
        from: parsed.from,
        to: parsed.to,
      });
    } catch (err) {
      setBalanceRows([]);
      setListMeta({
        mode: 'empty',
        total: 0,
        lastPage: 1,
        from: 0,
        to: 0,
      });
      setListError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          'Network error while loading stock.'
      );
    } finally {
      setListLoading(false);
    }
  }, [listPage, searchQ]);

  useEffect(() => {
    fetchBalanceList();
  }, [fetchBalanceList]);

  const tableRows = useMemo(
    () => dedupeBalanceRowsByProduct(balanceRows, categoryNameById),
    [balanceRows, categoryNameById]
  );

  const lowOnPage = useMemo(
    () =>
      tableRows.filter((r) => {
        if (r.inactive) return false;
        const rl = r.reorderLevel;
        if (rl != null && r.qty <= rl) return true;
        return r.qty <= LOW_STOCK;
      }).length,
    [tableRows]
  );

  async function handleQuickUpdate(e) {
    e.preventDefault();
    setQuickError(null);
    const productId = Number(quickProductId);
    if (!quickProductId || Number.isNaN(productId) || productId <= 0) {
      setQuickError('Select a product.');
      return;
    }
    const bal = parseInt(quickBalance, 10);
    if (Number.isNaN(bal) || bal < 0) {
      setQuickError('Enter a valid balance (0 or more).');
      return;
    }

    setQuickSubmitting(true);
    try {
      const resp = await setProductBalanceOnServer(productId, bal);
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        const msg = data?.errorMessage || 'Could not update balance.';
        setQuickError(msg);
        swalError('Could not update stock', msg);
        return;
      }
      setStock(String(productId), bal);
      setQuickBalance('');
      setQuickProductId('');
      fetchBalanceList();
      swalSuccess('Stock updated', 'Balance was saved.');
    } catch (err) {
      const msg =
        err?.response?.data?.errorMessage ||
        err?.message ||
        'Network error while updating balance.';
      setQuickError(msg);
      swalError('Could not update stock', msg);
    } finally {
      setQuickSubmitting(false);
    }
  }

  function applySearch(e) {
    e.preventDefault();
    setSearchQ(searchInput.trim());
    setListPage(1);
  }

  function clearSearch() {
    setSearchInput('');
    setSearchQ('');
    setListPage(1);
  }

  const statTotalLabel =
    listMeta.mode === 'paginator' ? listMeta.total : tableRows.length;
  const showFullPagination =
    listMeta.mode === 'paginator' && listMeta.total > 0;

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">Stock Management</h1>
        <p className="page-subtitle">
          Choose a product, set the balance, and update. The list below shows
          stock from the server (search to filter).
        </p>
      </header>

      <div className="stat-row">
        <div className="stat-card">
          <span className="stat-label">
            {listMeta.mode === 'paginator' ? 'Total records' : 'On this page'}
          </span>
          <span className="stat-value">{statTotalLabel}</span>
        </div>
        <div className="stat-card stat-card--warn">
          <span className="stat-label">Low stock (this page)</span>
          <span className="stat-value">{lowOnPage}</span>
        </div>
      </div>

      <section className="card">
        <div className="card-toolbar">
          <h2 className="card-title">Update stock</h2>
        </div>
        <form className="form-grid" onSubmit={handleQuickUpdate}>
          <label className="field">
            <span className="field-label">Name</span>
            <select
              className="input"
              value={quickProductId}
              onChange={(e) => {
                setQuickProductId(e.target.value);
                setQuickError(null);
              }}
              required
              disabled={productOptionsLoading || quickSubmitting}
            >
              <option value="">
                {productOptionsLoading ? 'Loading products…' : 'Select product'}
              </option>
              {productOptions.map((p) => (
                <option key={p.uuid || p.id} value={String(p.id)}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Balance</span>
            <input
              className="input"
              type="number"
              min={0}
              step={1}
              value={quickBalance}
              onChange={(e) => {
                setQuickBalance(e.target.value);
                setQuickError(null);
              }}
              placeholder="0"
              required
              disabled={quickSubmitting}
              aria-label="Balance quantity"
            />
          </label>
          <div className="field field--actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={quickSubmitting || productOptionsLoading}
            >
              {quickSubmitting ? 'Updating…' : 'Update'}
            </button>
          </div>
        </form>
        {quickError && (
          <p className="modal-error" role="alert" style={{ marginTop: '0.6rem' }}>
            {quickError}
          </p>
        )}
      </section>

      <section className="card">
        <div className="card-toolbar">
          <h2 className="card-title">Stock list</h2>
        </div>
        <form
          className="form-grid"
          style={{ marginBottom: '0.85rem' }}
          onSubmit={applySearch}
        >
          <label className="field">
            <span className="field-label">Search (q)</span>
            <input
              className="input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Type to search…"
              aria-label="Stock list search"
            />
          </label>
          <div className="field field--actions" style={{ alignSelf: 'flex-end' }}>
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </div>
          <div className="field field--actions" style={{ alignSelf: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={clearSearch}>
              Clear
            </button>
          </div>
        </form>

        {listError && (
          <p className="modal-error" role="alert" style={{ marginBottom: '0.6rem' }}>
            {listError}
          </p>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Balance</th>
                <th>Reorder level</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {listLoading && tableRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty-cell">
                    Loading…
                  </td>
                </tr>
              ) : !listLoading && tableRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty-cell">
                    {searchQ
                      ? 'No stock rows match your search.'
                      : 'No stock records yet.'}
                  </td>
                </tr>
              ) : (
                tableRows.map((r) => {
                  const rl = r.reorderLevel;
                  const belowReorder =
                    rl != null && r.qty <= rl;
                  const lowByThreshold =
                    !belowReorder && r.qty <= LOW_STOCK;
                  let statusLabel;
                  let statusClass;
                  if (r.inactive) {
                    statusLabel = 'Inactive';
                    statusClass = 'badge-muted';
                  } else if (belowReorder || lowByThreshold) {
                    statusLabel = 'Low';
                    statusClass = 'badge-warn';
                  } else {
                    statusLabel = 'OK';
                    statusClass = 'badge-ok';
                  }
                  return (
                    <tr key={r.id}>
                      <td>{r.name}</td>
                      <td>{r.category}</td>
                      <td className="mono">{r.qty}</td>
                      <td className="mono">
                        {rl != null ? String(rl) : '—'}
                      </td>
                      <td>
                        <span className={`badge ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {showFullPagination && (
          <Pagination
            page={listPage}
            totalPages={listMeta.lastPage}
            onPageChange={setListPage}
            total={listMeta.total}
            from={listMeta.from}
            to={listMeta.to}
          />
        )}

        {!showFullPagination &&
          listMeta.mode === 'array' &&
          tableRows.length > 0 && (
            <div
              className="pagination"
              role="navigation"
              aria-label="Stock pagination"
            >
              <span className="pagination-range">
                {(listPage - 1) * LIST_PER_PAGE + 1}–
                {(listPage - 1) * LIST_PER_PAGE + tableRows.length}
                {searchQ ? ` · search "${searchQ}"` : ''}
              </span>
              <div className="pagination-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  disabled={listPage <= 1}
                  onClick={() => setListPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  Previous
                </button>
                <span className="pagination-page mono">Page {listPage}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  disabled={tableRows.length < LIST_PER_PAGE}
                  onClick={() => setListPage((p) => p + 1)}
                  aria-label="Next page"
                >
                  Next
                </button>
              </div>
            </div>
          )}
      </section>
    </div>
  );
}

